/**
 * Full-viewport overlay showing all keyboard shortcuts.
 * Triggered by pressing `?`. Dismissed by Escape, click, or `?` again.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '@/store/uiStore.ts';
import styles from './KeyboardShortcutsOverlay.module.css';

const SHORTCUT_GROUPS = [
  {
    titleKey: 'shortcutsGroupPlayback',
    shortcuts: [
      { keys: 'Space', descKey: 'shortcutPlayPause' },
      { keys: 'J / K / L', descKey: 'shortcutShuttle' },
      { keys: '\u2190 / \u2192', descKey: 'shortcutSeek' },
      { keys: 'Home', descKey: 'shortcutJumpStart' },
      { keys: 'End', descKey: 'shortcutJumpEnd' },
      { keys: '⌘⌥→ / ⌘⌥←', descKey: 'shortcutNextPrevShot' },
    ],
  },
  {
    titleKey: 'shortcutsGroupEditing',
    shortcuts: [
      { keys: 'D', descKey: 'shortcutToggleDraw' },
      { keys: 'Del / Backspace', descKey: 'shortcutDelete' },
      { keys: '\u2318Z', descKey: 'shortcutUndo' },
      { keys: '\u2318\u21E7Z', descKey: 'shortcutRedo' },
      { keys: '\u2318S', descKey: 'shortcutSave' },
      { keys: '\u2318C', descKey: 'shortcutCopyShot' },
      { keys: '\u2318V', descKey: 'shortcutPasteShot' },
    ],
  },
  {
    titleKey: 'shortcutsGroupView',
    shortcuts: [
      { keys: 'F', descKey: 'shortcutToggleFullscreen' },
      { keys: 'E', descKey: 'shortcutToggleEnhanced' },
      { keys: 'C', descKey: 'shortcutToggleComments' },
      { keys: 'H', descKey: 'shortcutToggleHistory' },
      { keys: 'M', descKey: 'shortcutToggleMobile' },
      { keys: 'R', descKey: 'shortcutToggleStep' },
      { keys: '+ / \u2013', descKey: 'shortcutZoomInOut' },
    ],
  },
  {
    titleKey: 'shortcutsGroupQuickAi',
    shortcuts: [
      { keys: '\u2325A', descKey: 'shortcutAudioWorkspace' },
      { keys: '\u2325I', descKey: 'shortcutQuickEnhance' },
      { keys: '\u2325V', descKey: 'shortcutQuickVideo' },
      { keys: '\u2325S', descKey: 'shortcutQuickSpeech' },
    ],
  },
  {
    titleKey: 'shortcutsGroupHelp',
    shortcuts: [
      { keys: 'G', descKey: 'shortcutPolishShare' },
      { keys: '?', descKey: 'shortcutShowOverlay' },
    ],
  },
];

export function KeyboardShortcutsOverlay() {
  const { t } = useTranslation('transport');
  const open = useUiStore(s => s.shortcutsOverlayOpen);
  const toggle = useUiStore(s => s.toggleShortcutsOverlay);

  // Dismiss on Escape — delayed listener to avoid the `?` keypress from immediately closing
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const timerId = setTimeout(() => {
      if (!mounted) return;
      const handler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          toggle();
        }
      };
      window.addEventListener('keydown', handler);
      // Store cleanup
      cleanupRef = () => window.removeEventListener('keydown', handler);
    }, 0);
    let cleanupRef: (() => void) | null = null;
    return () => {
      mounted = false;
      clearTimeout(timerId);
      cleanupRef?.();
    };
  }, [open, toggle]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={toggle}>
      <div className={styles.panel} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.title}>{t('shortcutsOverlayTitle')}</span>
          <button className={styles.closeBtn} onClick={toggle}>&times;</button>
        </div>
        <div className={styles.grid}>
          {SHORTCUT_GROUPS.map(group => (
            <div key={group.titleKey} className={styles.group}>
              <div className={styles.groupTitle}>{t(group.titleKey)}</div>
              {group.shortcuts.map(sc => (
                <div key={sc.keys} className={styles.row}>
                  <kbd className={styles.key}>{sc.keys}</kbd>
                  <span className={styles.desc}>{t(sc.descKey)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
